import * as Complex from 'complex.js'
import * as Polynomial from 'polynomial'
import * as PolyRoots from 'durand-kerner'

/**
 * Features:
 * - Get poles and zeros.
 * - Get numerator and denominator coefficients.
 */
export default class TransferFunction {
  numerator: Polynomial
  denominator: Polynomial
  poles: Complex[]
  zeros: Complex[]

  constructor(arg1, arg2?) {
    if (typeof(arg1) == 'string' && arg2 == undefined) {
      this.fromEquation(arg1 as string)
    } else if (arg1 instanceof Array && arg2 instanceof Array) {
      this.fromPolesZeros(arg1 as Complex[], arg2 as Complex[])
    } else if (arg1 instanceof Polynomial && arg2 instanceof Polynomial) {
      this.fromPolynomials(arg1 as Polynomial, arg2 as Polynomial)
    } else {
      console.error('Unknown arguments supplied in TransferFunction constructor.')
    }
  }

  private fromEquation(equation: string) {
    const [numString, denString] = equation.split('/')
    const numerator = parsePolynomial(numString)
    const denominator = parsePolynomial(denString)

    this.fromPolynomials(numerator, denominator)
  }

  private fromPolesZeros(poles: Complex[], zeros: Complex[]) {
    // Create a series of 1st order polynomials, one for each complex root, and
    // calculate all coefficients by multiplying them all together.
    this.numerator = polynomialFromRoots(zeros)
    this.denominator = polynomialFromRoots(poles)

    this.zeros = zeros
    this.poles = poles
  }

  private fromPolynomials(numerator: Polynomial, denominator: Polynomial) {
    this.numerator = numerator
    this.denominator = denominator
    this.zeros = findRoots(numerator)
    this.poles = findRoots(denominator)
  }

  isValid() {
    return this.numerator && this.denominator
  }

  /**
   * IIR filters will be unstable iff ∃ pole : |pole| < 1.
   * TODO: double check theory. According to https://goo.gl/Je1mxn, it should be
   * unstable iff |pole| > 1.
   */
  isStable() {
    for (let pole of this.poles) {
      if (pole.abs() > 1) {
        return false
      }
    }
    return true
  }

  getPoles() {
    return this.poles
  }

  getZeros() {
    return this.zeros
  }

  getFeedForward() {
    return getRealCoefficients(this.numerator).reverse()
  }

  getFeedBack() {
    return getRealCoefficients(this.denominator).reverse()
  }

  result(arg1, arg2) {
    let value = null
    if (typeof(arg1) == 'number' && typeof(arg2) == 'number') {
      value = new Complex(arg1, arg2)
    } else if (arg1 instanceof Complex && arg2 == undefined) {
      value = arg1
    } else {
      console.error('TransferFunction.result got arguments of the wrong type', arg1, arg2)
    }
    // Mostly from the Chromium implementation: https://goo.gl/c9DQeA
    const one = new Complex(1)
    const z = value.exp()
    const z1 = one.div(z)
    return this.numerator.result(value).div(this.denominator.result(value))
  }

  toString(precision=null) {
    // Use limited precision, and use s instead of x.
    const num = formatPolynomial(this.numerator, precision)
    const den = formatPolynomial(this.denominator, precision)
    return `(${num})/(${den})`
  }

}


function parsePolynomial(polyString: string) {
  // Parse out the numerator into a polynomial.
  polyString = polyString.replace(')', '').replace('(', '')
    .replace(/ /g, '') // Remove extra spaces.
    .replace(/z/g, 'x') // Rename 'z' to 'x'.
  try {
    const poly = new Polynomial(polyString)
    return poly
  } catch (e) {
    return null
  }
}

function getOrder(poly: Polynomial) {
  return Math.max.apply(null, Object.keys(poly.coeff))
}

function getRealCoefficients(poly: Polynomial) {
  if (!poly) {
    return null
  }
  let coeff = []
  let order = getOrder(poly)
  for (let i = 0; i <= order; i++) {
    let k = poly.coeff[i] || 0
    // Ensure we're only returning real coefficients.
    // TODO(smus): This may be dangerous.
    if (k instanceof Complex) {
      k = k.re
    }
    coeff[i] = k
  }
  return coeff
}

function findRoots(poly: Polynomial) {
  if (poly == null) {
    return null
  }
  const coeff = getRealCoefficients(poly)
  const raw = PolyRoots(coeff)

  let roots = []
  // Sometimes there are no roots, eg. if dealing with a constant.
  if (!raw.length) {
    return roots
  }
  for (let i = 0; i < raw[0].length; i++) {
    let real = raw[0][i]
    let imag = raw[1][i]
    roots.push(new Complex(real, imag))
  }

  for (let root of roots) {
    const result = poly.result(root).abs()
    if (result > 1e-5) {
      console.warn('Possibly found false root!', root)
    }
  }

  //console.log(`Sanity checking: ${poly} zeros are ${zeros}.`)
  return roots
}

function polynomialFromRoots(roots: Complex[]) {
  const polys = roots.map(z => new Polynomial([z.neg(), 1])
  if (polys.length == 0) {
    return new Polynomial([1])
  } else if (polys.length == 1) {
    return polys[0]
  }
  let result = polys[0]
  for (let poly of polys.slice(1)) {
    result = result.mul(poly)
  }
  return result
}

function formatPolynomial(poly: Polynomial, precision=null) {
  let coeff = getRealCoefficients(poly)
  if (precision) {
    coeff = coeff.map(k => eval(k.toPrecision(precision)))
  }
  let out = new Polynomial(coeff)
  return out.toString().replace(/x/g, 'z')
}

// DEBUG ONLY:
/*
window.TransferFunction = TransferFunction
window.Complex = Complex
window.Polynomial = Polynomial
window.PolyRoots = PolyRoots
window.getRealCoefficients = getRealCoefficients
 */



// WEBPACK FOOTER //
// ./src/TransferFunction.ts